import { useState } from 'react';

import { GridValidRowModel } from '@mui/x-data-grid';
import { AsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from 'app/store';
import { RequestParamsBase } from 'common/services/api/Models/CommonRequestModels';
import { DropDownInfo } from 'common/types/TypeDefs';

import { usePalmFetchEntities } from './usePalmFetchEntities';
import { usePalmFilters } from './usePalmFilters';
import { usePalmSortModel } from './usePalmSortModel';

interface FilterActions {
    selectFilters: (state: RootState) => { keywords: string[] };
    addNewFilter: (payload: string) => { payload: string; type: string };
    removeFilter: (payload: string) => { payload: string; type: string };
    clearFilters: () => { payload: undefined; type: string };
}

interface UsePalmTabStateOptions<TEntity extends GridValidRowModel, TThunkParams extends RequestParamsBase | undefined> {
    /**
     * Entity type name for error messages (e.g., 'Claim', 'User Group')
     */
    typeName: string;
    /**
     * Parameters to pass to the fetch thunk
     */
    fetchParams: TThunkParams;
    /**
     * The async thunk for fetching entities
     */
    fetchThunk: AsyncThunk<TEntity[], TThunkParams, Record<string, unknown>>;
    /**
     * Filter-related actions from the slice
     */
    filterActions: FilterActions;
    /**
     * Optional dropdown props for filter configuration
     */
    dropdownProps?: DropDownInfo[];
    /**
     * Optional initial sort field (defaults to 'name')
     */
    sortField?: string;
}

/**
 * Hook that consolidates common state management for client-side paginated tab components.
 * Combines processing state, sort model, filters, and entity fetching.
 */
export function usePalmTabState<TEntity extends GridValidRowModel, TThunkParams extends RequestParamsBase | undefined>({
    typeName,
    fetchParams,
    fetchThunk,
    filterActions,
    dropdownProps,
    sortField,
}: UsePalmTabStateOptions<TEntity, TThunkParams>) {
    const [processing, setProcessing] = useState<boolean>(false);
    const [sortModel, setSortModel] = usePalmSortModel(sortField);

    const filterProp = usePalmFilters(
        filterActions.selectFilters,
        filterActions.addNewFilter,
        filterActions.removeFilter,
        filterActions.clearFilters,
        dropdownProps
    );

    const fetchEntities = usePalmFetchEntities(
        typeName,
        fetchParams,
        fetchThunk,
        setProcessing
    );

    return {
        processing,
        sortModel,
        setSortModel,
        filterProp,
        fetchEntities,
    };
}
